import { useState, useEffect, useCallback, useRef } from "react";

// ── Types ──────────────────────────────────────────────────────────────

interface PreviewResult {
  ok: boolean;
  profileId?: string;
}

interface HeaderTreeEditorProps {
  raw: string;
  format: "xml" | "json";
  onPreview: (
    raw: string,
    format: "xml" | "json",
  ) => Promise<PreviewResult>;
}

// ── XML tree node representation ───────────────────────────────────────

interface XmlNode {
  tagName: string;
  attributes: Array<{ name: string; value: string }>;
  children: XmlNode[];
}

function domToTree(el: Element): XmlNode {
  const attributes: Array<{ name: string; value: string }> = [];
  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes.item(i);
    if (attr) {
      attributes.push({ name: attr.name, value: attr.value });
    }
  }
  const children: XmlNode[] = [];
  for (let i = 0; i < el.children.length; i++) {
    const child = el.children.item(i);
    if (child) {
      children.push(domToTree(child));
    }
  }
  return { tagName: el.tagName, attributes, children };
}

function treeToXml(node: XmlNode): string {
  const attrs = node.attributes
    .map((a) => `${a.name}="${a.value}"`)
    .join(" ");
  const attrStr = attrs ? ` ${attrs}` : "";
  if (node.children.length === 0) {
    return `<${node.tagName}${attrStr} />`;
  }
  const childXml = node.children.map(treeToXml).join("");
  return `<${node.tagName}${attrStr}>${childXml}</${node.tagName}>`;
}

// ── JSON tree helpers ──────────────────────────────────────────────────

interface JsonLeaf {
  path: string[];
  key: string;
  value: string;
}

function flattenJson(
  obj: unknown,
  path: string[] = [],
): JsonLeaf[] {
  const leaves: JsonLeaf[] = [];
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        leaves.push(...flattenJson(v, [...path, k]));
      } else {
        leaves.push({ path: [...path, k], key: k, value: String(v) });
      }
    }
  }
  return leaves;
}

function rebuildJson(
  original: Record<string, unknown>,
  leaves: JsonLeaf[],
): Record<string, unknown> {
  const result = JSON.parse(JSON.stringify(original)) as Record<string, unknown>;
  for (const leaf of leaves) {
    let cursor: Record<string, unknown> = result;
    for (let i = 0; i < leaf.path.length - 1; i++) {
      const segment = leaf.path[i];
      if (segment !== undefined) {
        cursor = cursor[segment] as Record<string, unknown>;
      }
    }
    const lastSegment = leaf.path[leaf.path.length - 1];
    if (lastSegment !== undefined) {
      cursor[lastSegment] = leaf.value;
    }
  }
  return result;
}

// ── XML tree node renderer ─────────────────────────────────────────────

function XmlNodeView({
  node,
  onAttrChange,
}: {
  node: XmlNode;
  onAttrChange: (tagName: string, attrName: string, value: string) => void;
}) {
  return (
    <div style={{ paddingLeft: 12 }}>
      <span style={{ fontWeight: 600 }}>&lt;{node.tagName}</span>
      {node.attributes.map((attr) => (
        <span key={attr.name} style={{ marginLeft: 8 }}>
          {attr.name}=&quot;
          <input
            value={attr.value}
            onChange={(e) =>
              onAttrChange(node.tagName, attr.name, e.target.value)
            }
            style={{ width: Math.max(40, attr.value.length * 8 + 16) }}
          />
          &quot;
        </span>
      ))}
      {node.children.length === 0 ? (
        <span> /&gt;</span>
      ) : (
        <>
          <span>&gt;</span>
          {node.children.map((child, i) => (
            <XmlNodeView
              key={`${child.tagName}-${i}`}
              node={child}
              onAttrChange={onAttrChange}
            />
          ))}
          <div>
            &lt;/{node.tagName}&gt;
          </div>
        </>
      )}
    </div>
  );
}

// ── Preview pill ───────────────────────────────────────────────────────

function PreviewPill({ preview }: { preview: PreviewResult | null }) {
  if (!preview) return null;
  if (preview.ok) {
    return (
      <span
        style={{
          background: "#4CAF50",
          color: "#fff",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: 13,
        }}
      >
        Resolves to profile {preview.profileId ?? "unknown"}
      </span>
    );
  }
  return (
    <span
      style={{
        background: "#F44336",
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 13,
      }}
    >
      Still failing
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export function HeaderTreeEditor({
  raw,
  format,
  onPreview,
}: HeaderTreeEditorProps) {
  const [current, setCurrent] = useState(raw);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;

  // Debounced preview
  useEffect(() => {
    const t = setTimeout(() => {
      onPreviewRef.current(current, format).then(setPreview);
    }, 400);
    return () => clearTimeout(t);
  }, [current, format]);

  // ── Parse attempt ──────────────────────────────────────────────────

  let xmlTree: XmlNode | null = null;
  let jsonObj: Record<string, unknown> | null = null;
  let jsonLeaves: JsonLeaf[] = [];

  if (format === "xml") {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(current, "text/xml");
      if (!doc.querySelector("parsererror") && doc.documentElement) {
        xmlTree = domToTree(doc.documentElement);
      }
    } catch {
      // fall through to textarea
    }
  } else {
    try {
      const parsed = JSON.parse(current) as Record<string, unknown>;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        jsonObj = parsed;
        jsonLeaves = flattenJson(parsed);
      }
    } catch {
      // fall through to textarea
    }
  }

  // ── Fallback: textarea ─────────────────────────────────────────────

  if (!xmlTree && !jsonObj) {
    return (
      <div>
        <textarea
          role="textbox"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          rows={8}
          style={{ width: "100%", fontFamily: "monospace" }}
        />
        <PreviewPill preview={preview} />
      </div>
    );
  }

  // ── XML tree mode ──────────────────────────────────────────────────

  if (xmlTree) {
    const handleAttrChange = (
      _tagName: string,
      attrName: string,
      value: string,
    ) => {
      // Update the tree and serialize back
      const updateNode = (node: XmlNode): XmlNode => ({
        ...node,
        attributes: node.attributes.map((a) =>
          a.name === attrName ? { ...a, value } : a,
        ),
        children: node.children.map(updateNode),
      });
      const updated = updateNode(xmlTree!);
      setCurrent(treeToXml(updated));
    };

    return (
      <div>
        <XmlNodeView node={xmlTree} onAttrChange={handleAttrChange} />
        <PreviewPill preview={preview} />
      </div>
    );
  }

  // ── JSON tree mode ─────────────────────────────────────────────────

  const handleJsonLeafChange = useCallback(
    (leafIndex: number, value: string) => {
      const updatedLeaves = jsonLeaves.map((l, i) =>
        i === leafIndex ? { ...l, value } : l,
      );
      const rebuilt = rebuildJson(jsonObj!, updatedLeaves);
      setCurrent(JSON.stringify(rebuilt, null, 2));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current],
  );

  return (
    <div>
      {jsonLeaves.map((leaf, i) => (
        <div key={leaf.path.join(".")} style={{ marginBottom: 4 }}>
          <label style={{ marginRight: 8, fontFamily: "monospace" }}>
            {leaf.path.join(".")}:
          </label>
          <input
            value={leaf.value}
            onChange={(e) => handleJsonLeafChange(i, e.target.value)}
          />
        </div>
      ))}
      <PreviewPill preview={preview} />
    </div>
  );
}
